import type { Config } from '@ml/config';
import type { Rng } from '../rng.ts';
import { hasOverlay } from './grid.ts';
import {
  getLinkCandidates,
  isBoosterLike,
  isMonochrome,
  isPrefixOf,
  NO_PATTERNS,
  seqTokenOf,
  type LinkContext,
} from './link.ts';
import { generateBoard, regenerateUncovered, type SpawnConfig } from './spawn.ts';
import type { Board, Coord, SeqToken } from './types.ts';

export type MoveConfig = Pick<Config, 'link' | 'essence'>;
export type PlayableConfig = SpawnConfig & Pick<Config, 'link'>;

/**
 * Chuỗi `path` có là một nước hợp lệ hoàn chỉnh không (core-loop 3.5): phân loại được thành
 * MERGE, BOOSTER_TAP, BOOSTER_COMBO, ABILITY/ULTIMATE (khớp đúng một dãy trong ctx) hoặc
 * NORMAL (đơn sắc, `L >= link.minLength`). Không kiểm hình học (dùng validatePath).
 */
export function isCompleteMove(
  board: Board,
  path: readonly Coord[],
  config: MoveConfig,
  ctx: LinkContext = NO_PATTERNS,
): boolean {
  const cells = path.map((c) => board.cells[c.y * board.width + c.x] ?? null);
  if (cells.some((c) => c === null)) return false;
  const seq: SeqToken[] = [];
  let essence = 0;
  for (const c of cells) {
    const t = seqTokenOf(c!);
    if (t === null) essence++;
    else seq.push(t);
  }
  if (seq.length === 0) return essence >= config.essence.mergeMinCount;
  if (cells.length <= 2 && cells.every(isBoosterLike)) return true;
  if (ctx.patterns.some((p) => p.length === seq.length && isPrefixOf(seq, p))) return true;
  return seq.length >= config.link.minLength && isMonochrome(seq);
}

/**
 * Đếm nước hợp lệ (chuỗi có thứ tự, khác nhau) bằng DFS theo ứng viên nối, dừng khi đạt
 * `limit`. Luôn tỉa bằng lọc tiền tố (mọi tiền tố của một nước hợp lệ đều qua lọc), độ dài
 * `L` tối đa = max(minLength, độ dài dãy dài nhất trong ctx). Thứ tự duyệt tất định.
 */
export function countValidMoves(
  board: Board,
  config: MoveConfig,
  ctx: LinkContext = NO_PATTERNS,
  limit = 1,
): number {
  const searchConfig = { link: { ...config.link, prefixFilter: true } };
  const maxL = Math.max(config.link.minLength, ...ctx.patterns.map((p) => p.length));
  let count = 0;
  const visit = (path: Coord[], L: number): void => {
    if (count >= limit) return;
    if (isCompleteMove(board, path, config, ctx)) {
      count++;
      if (count >= limit) return;
    }
    for (const c of getLinkCandidates(board, path, searchConfig, ctx)) {
      const cell = board.cells[c.y * board.width + c.x]!;
      const nextL = L + (seqTokenOf(cell) === null ? 0 : 1);
      if (nextL > maxL) continue;
      path.push(c);
      visit(path, nextL);
      path.pop();
      if (count >= limit) return;
    }
  };
  for (const start of getLinkCandidates(board, [], searchConfig, ctx)) {
    const cell = board.cells[start.y * board.width + start.x]!;
    visit([start], seqTokenOf(cell) === null ? 0 : 1);
    if (count >= limit) break;
  }
  return count;
}

export function hasValidMove(
  board: Board,
  config: MoveConfig,
  ctx: LinkContext = NO_PATTERNS,
): boolean {
  return countValidMoves(board, config, ctx, 1) >= 1;
}

/** Giới hạn an toàn cho vòng sinh lại (luôn tất định; vượt thì báo lỗi config). */
export const MAX_REGENERATE_ATTEMPTS = 1000;

/**
 * Bảng ban đầu (core-loop 3.5): sinh theo thứ tự hàng; nếu ít hơn `board.initialMinValidMoves`
 * nước hợp lệ thì sinh lại toàn bộ, tiếp tục dùng cùng RNG.
 */
export function createInitialBoard(
  rng: Rng,
  config: PlayableConfig,
  ctx: LinkContext = NO_PATTERNS,
): Board {
  const need = config.board.initialMinValidMoves;
  for (let i = 0; i < MAX_REGENERATE_ATTEMPTS; i++) {
    const board = generateBoard(rng, config);
    if (countValidMoves(board, config, ctx, need) >= need) return board;
  }
  throw new Error(`không sinh được bảng có ${need} nước hợp lệ sau ${MAX_REGENERATE_ATTEMPTS} lần`);
}

/**
 * Xáo Fisher–Yates các viên không có lớp phủ (viên khoá/băng/đánh dấu đứng yên). Vị trí lấy
 * theo thứ tự hàng; `for i = n-1 → 1: j = nextInt(i+1); swap(i, j)`. Ô rỗng cũng được xáo
 * (bình thường bảng đã đầy khi xáo).
 */
export function shuffleUncovered(board: Board, rng: Rng): Board {
  const positions: number[] = [];
  board.cells.forEach((c, i) => {
    if (!hasOverlay(c)) positions.push(i);
  });
  const items = positions.map((i) => board.cells[i] ?? null);
  for (let i = items.length - 1; i >= 1; i--) {
    const j = rng.nextInt(i + 1);
    const tmp = items[i]!;
    items[i] = items[j]!;
    items[j] = tmp;
  }
  const cells = board.cells.slice();
  positions.forEach((p, k) => {
    cells[p] = items[k] ?? null;
  });
  return { width: board.width, height: board.height, cells };
}

export type EnsureMovesResult = {
  readonly board: Board;
  /** none: đã có nước / tắt auto-shuffle; shuffled: xáo `attempts` lần; regenerated: sinh lại. */
  readonly action: 'none' | 'shuffled' | 'regenerated';
  readonly attempts: number;
  readonly hasMove: boolean;
};

/**
 * Sau mỗi lượt (core-loop 3.5, orbs R9): hết nước và `board.autoShuffleWhenNoMoves` → xáo tối
 * đa `board.shuffleMaxAttempts` lần; vẫn không có thì sinh lại các ô không lớp phủ (lặp tới
 * khi có nước). Không tốn lượt.
 */
export function ensureMoves(
  board: Board,
  rng: Rng,
  config: PlayableConfig,
  ctx: LinkContext = NO_PATTERNS,
): EnsureMovesResult {
  if (hasValidMove(board, config, ctx))
    return { board, action: 'none', attempts: 0, hasMove: true };
  if (!config.board.autoShuffleWhenNoMoves) {
    return { board, action: 'none', attempts: 0, hasMove: false };
  }
  let b = board;
  for (let i = 1; i <= config.board.shuffleMaxAttempts; i++) {
    b = shuffleUncovered(b, rng);
    if (hasValidMove(b, config, ctx))
      return { board: b, action: 'shuffled', attempts: i, hasMove: true };
  }
  for (let i = 1; i <= MAX_REGENERATE_ATTEMPTS; i++) {
    b = regenerateUncovered(b, rng, config);
    if (hasValidMove(b, config, ctx)) {
      return { board: b, action: 'regenerated', attempts: i, hasMove: true };
    }
  }
  return { board: b, action: 'regenerated', attempts: MAX_REGENERATE_ATTEMPTS, hasMove: false };
}
